import { useState, useCallback, useEffect } from "react";

import { ALL_ONBOARDING_STEPS } from "./onboardingSteps";

const ONBOARDING_STATE_CHANGED_EVENT = "onboarding-state-changed";

function getStorageKey(userId: string): string {
  return `onboarding_${userId}`;
}

type StepCompletions = Record<string, boolean>;

function loadCompletions(userId: string): StepCompletions {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (raw) return JSON.parse(raw);
  } catch {
    // Corrupted data — start fresh
  }
  return {};
}

function saveCompletions(userId: string, completions: StepCompletions): void {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(completions));
  } catch {
    // localStorage full or unavailable — silent fail
  }
}

export type OnboardingStepState = {
  id: string;
  completed: boolean;
};

export type UseOnboardingStateReturn = {
  steps: OnboardingStepState[];
  completeStep: (stepId: string) => void;
  resetSteps: () => void;
  completedCount: number;
  totalSteps: number;
  allCompleted: boolean;
};

export const useOnboardingState = (userId: string | undefined): UseOnboardingStateReturn => {
  const [completions, setCompletions] = useState<StepCompletions>(() => (userId ? loadCompletions(userId) : {}));

  // Re-load from localStorage when userId becomes available (auth loads async)
  useEffect(() => {
    if (userId) {
      setCompletions(loadCompletions(userId));
    }
  }, [userId]);

  // Sync across multiple hook instances in the same tab
  useEffect(() => {
    const handler = () => {
      if (userId) setCompletions(loadCompletions(userId));
    };
    window.addEventListener(ONBOARDING_STATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_STATE_CHANGED_EVENT, handler);
  }, [userId]);

  const completeStep = useCallback(
    (stepId: string) => {
      if (!userId) return;
      setCompletions((prev) => {
        const updated = { ...prev, [stepId]: true };
        saveCompletions(userId, updated);
        window.dispatchEvent(new CustomEvent(ONBOARDING_STATE_CHANGED_EVENT));
        return updated;
      });
    },
    [userId],
  );

  const resetSteps = useCallback(() => {
    if (!userId) return;
    saveCompletions(userId, {});
    setCompletions({});
    // Clear coach overlay "seen" flags so driver.js replays them
    try {
      const prefix = `onboarding_overlay_`;
      const suffix = `_${userId}`;
      Object.keys(localStorage)
        .filter((key) => key.startsWith(prefix) && key.endsWith(suffix))
        .forEach((key) => localStorage.removeItem(key));
    } catch {
      // silent
    }
    window.dispatchEvent(new CustomEvent(ONBOARDING_STATE_CHANGED_EVENT));
  }, [userId]);

  const steps: OnboardingStepState[] = ALL_ONBOARDING_STEPS.map((step) => ({
    id: step.id,
    completed: !!completions[step.id],
  }));

  const completedCount = steps.filter((s) => s.completed).length;
  const totalSteps = steps.length;
  const allCompleted = completedCount === totalSteps;

  return {
    steps,
    completeStep,
    resetSteps,
    completedCount,
    totalSteps,
    allCompleted,
  };
};
